import { WatchersCard } from '@/components/watchers';
import { RateSelect } from '@/components/rates';
import { BillingCard, LockBanner, useUnlock } from '@/components/billing';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Clock, Download, Link2, Lock, MessageSquare, Pencil, Trash2, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CustomerPicker } from '@/components/customer-picker';
import { AppointmentsCard } from '@/components/appointments';
import { ChecklistsCard } from '@/components/checklists';
import { MonitoringCard } from '@/components/monitoring';
import { SlaCard } from '@/components/sla';
import { RepairStatusBadge } from '@/components/repairs';
import { TicketDevicesCard } from '@/components/ticket-devices';
import { AreaBadge, describeEvent, formatBytes, formatMinutes, PriorityLabel, StatusBadge, UploadButton } from '@/components/tickets';
import { PrintButton } from '@/components/printing';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardHeader, CardTitle, CollapsibleCards, Input, Label, Select, Skeleton, Textarea } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { CustomerDetail, Priority, TicketDetail, Visibility } from '@/lib/types';
import { cn, formatDate, formatDateTime, formatRelative } from '@/lib/utils';
import { useTicketMeta } from './tickets-page';

type Patch = Partial<{
  statusId: string;
  priority: Priority;
  assigneeId: string | null;
  teamId: string | null;
  areaId: string;
  categoryId: string | null;
  customerId: string | null;
  contactId: string | null;
  dueAt: string | null;
  followUpAt: string | null;
  subject: string;
  description: string;
}>;

function toLocalInput(iso: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function Prop({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Composer({ ticketId, canInternal, canPublic, onDone }: { ticketId: string; canInternal: boolean; canPublic: boolean; onDone: () => void }) {
  const [mode, setMode] = useState<Visibility>(canPublic ? 'PUBLIC' : 'INTERNAL');
  const [body, setBody] = useState('');
  const send = useMutation({
    mutationFn: () =>
      api(`/tickets/${ticketId}/comments`, {
        method: 'POST',
        body: { body, visibility: mode },
      }),
    onSuccess: () => {
      setBody('');
      toast.success(mode === 'PUBLIC' ? 'Antwort gesendet' : 'Notiz gespeichert');
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('visibility', mode);
      form.append('file', file);
      return api(`/tickets/${ticketId}/attachments`, { method: 'POST', form });
    },
    onSuccess: () => {
      toast.success('Datei angehängt');
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!canInternal && !canPublic) return null;
  const internal = mode === 'INTERNAL';
  return (
    <Card className={cn('overflow-hidden', internal && 'border-amber-400/60')}>
      <div className="flex border-b text-sm">
        {canPublic && (
          <button className={cn('flex items-center gap-1.5 px-4 py-2 font-medium', !internal ? 'bg-card text-foreground' : 'bg-muted/50 text-muted-foreground')} onClick={() => setMode('PUBLIC')}>
            <MessageSquare className="size-4" /> Antwort an Kunden
          </button>
        )}
        {canInternal && (
          <button
            className={cn('flex items-center gap-1.5 px-4 py-2 font-medium', internal ? 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' : 'bg-muted/50 text-muted-foreground')}
            onClick={() => setMode('INTERNAL')}
          >
            <Lock className="size-4" /> Interne Notiz
          </button>
        )}
      </div>
      <div className={cn('p-3', internal && 'bg-amber-50/60 dark:bg-amber-950/20')}>
        <Textarea
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={internal ? 'Nur für Mitarbeiter sichtbar …' : 'Der Kunde erhält diese Nachricht per E-Mail und im Portal …'}
          aria-label={internal ? 'Interne Notiz' : 'Antwort'}
        />
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <UploadButton onFile={(f) => upload.mutate(f)} loading={upload.isPending} label={internal ? 'Interne Datei' : 'Datei für Kunden'} />
          <Button onClick={() => send.mutate()} disabled={!body.trim()} loading={send.isPending} variant={internal ? 'secondary' : 'default'}>
            {internal ? 'Notiz speichern' : 'Antwort senden'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function EditDialog({ ticket, open, onOpenChange, onSave }: { ticket: TicketDetail; open: boolean; onOpenChange: (o: boolean) => void; onSave: (p: Patch) => void }) {
  const [subject, setSubject] = useState(ticket.subject);
  const [description, setDescription] = useState(ticket.description);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>Ticket bearbeiten</DialogTitle>
        </DialogHeader>
        <DialogBody className="grid gap-4">
          <Field label="Betreff" htmlFor="e-subject">
            <Input id="e-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="Beschreibung" htmlFor="e-desc">
            <Textarea id="e-desc" rows={10} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            disabled={subject.trim().length < 3 || !description.trim()}
            onClick={() => {
              onSave({ subject, description });
              onOpenChange(false);
            }}
          >
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, me } = useAuth();
  const { data: meta } = useTicketMeta();
  const [confirmDialog, confirm] = useConfirm();
  const [editOpen, setEditOpen] = useState(false);
  const [minutes, setMinutes] = useState('');
  const [timeNote, setTimeNote] = useState('');
  const [rateId, setRateId] = useState('');
  const [linkNumber, setLinkNumber] = useState('');

  const { data: t, error } = useQuery({
    queryKey: ['tickets', 'detail', id],
    queryFn: () => api<TicketDetail>(`/tickets/${id}`),
  });
  const unlock = useUnlock(`/tickets/${id}`, t?.billingLock);
  const { data: customer } = useQuery({
    queryKey: ['customers', t?.customer?.id],
    queryFn: () => api<CustomerDetail>(`/customers/${t!.customer!.id}`),
    enabled: Boolean(t?.customer && can('customers.read')),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['billing', 'ref'] });
    qc.invalidateQueries({ queryKey: ['tickets'] });
  };
  const onError = (e: unknown) => toast.error(errorMessage(e));

  const patch = useMutation({
    mutationFn: (p: Patch) => api(`/tickets/${id}`, { method: 'PATCH', body: p }),
    onSuccess: refresh,
    onError,
  });
  const addTime = useMutation({
    mutationFn: () =>
      api(`/tickets/${id}/time-entries`, {
        method: 'POST',
        body: {
          minutes: Number(minutes),
          description: timeNote || null,
          workDate: new Date().toISOString().slice(0, 10),
          rateId: rateId || null,
        },
      }),
    onSuccess: () => {
      setMinutes('');
      setTimeNote('');
      refresh();
    },
    onError,
  });
  const delTime = useMutation({
    mutationFn: (entryId: string) => api(`/tickets/${id}/time-entries/${entryId}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError,
  });
  const addLink = useMutation({
    mutationFn: () =>
      api(`/tickets/${id}/links`, {
        method: 'POST',
        body: { number: linkNumber.trim() },
      }),
    onSuccess: () => {
      setLinkNumber('');
      refresh();
    },
    onError,
  });
  const delLink = useMutation({
    mutationFn: (otherId: string) => api(`/tickets/${id}/links/${otherId}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError,
  });
  const remove = useMutation({
    mutationFn: () => api(`/tickets/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Ticket gelöscht');
      qc.invalidateQueries({ queryKey: ['tickets'] });
      navigate('/tickets');
    },
    onError,
  });

  const names = useMemo(
    () => ({
      agents: new Map(meta?.agents.map((a) => [a.id, a.name])),
      teams: new Map(meta?.teams.map((x) => [x.id, x.name])),
    }),
    [meta],
  );

  const timeline = useMemo(() => {
    if (!t) return [];
    const comments = t.comments.map((c) => ({
      kind: 'comment' as const,
      at: c.createdAt,
      comment: c,
    }));
    const events = t.events.filter((e) => e.type !== 'comment_added').map((e) => ({ kind: 'event' as const, at: e.createdAt, event: e }));
    return [...comments, ...events].sort((a, b) => a.at.localeCompare(b.at));
  }, [t]);

  if (error instanceof ApiError && error.status === 404) {
    return <p className="py-20 text-center text-muted-foreground">Ticket nicht gefunden oder kein Zugriff.</p>;
  }
  if (!t || !meta) return <Skeleton className="h-96" />;

  const canUpdate = can('tickets.update');
  // Ab „Bereit zur Rechnung“ sind abrechnungsrelevante Angaben gesperrt (Änderungsantrag)
  const billingEditable = canUpdate && (!t.billingLock.kind || (t.billingLock.canOverride && unlock.unlocked));
  const canAssign = can('tickets.assign');
  const totalMinutes = t.timeEntries.reduce((s, e) => s + e.minutes, 0);
  const categories = meta.categories.filter((c) => !c.areaId || c.areaId === t.area.id);

  return (
    <>
      {confirmDialog}
      <div className="mb-4">
        <Link to="/tickets" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" /> Tickets
        </Link>
      </div>
      <LockBanner lock={t.billingLock} unlock={unlock} entity="ticket" />
      {t.billingQuery && (
        <Alert className="mb-4">
          Rückfrage der Buchhaltung zu{' '}
          <Link to={t.billingQuery.kind === 'repair' ? `/repairs/${t.billingQuery.id}` : `/tickets/${t.billingQuery.id}`} className="font-medium text-primary hover:underline">
            {t.billingQuery.kind === 'repair' ? 'Reparatur' : 'Ticket'} {t.billingQuery.number}
          </Link>
          . Nach der Klärung „Geklärt – zur Abrechnung“ wählen: Der Vorgang steht dann automatisch wieder zur Abrechnung bereit
          {t.billingQuery.kind === 'repair' ? ' und das Gerät wird nach der Rechnung abholbereit' : ''}.
        </Alert>
      )}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-mono text-muted-foreground">{t.number}</span>
            <StatusBadge name={t.status.name} color={t.status.color} />
            <AreaBadge name={t.area.name} color={t.area.color} />
            <PriorityLabel priority={t.priority} />
          </div>
          <h1 className="mt-1.5 text-2xl font-semibold tracking-tight">{t.subject}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Erstellt {formatDateTime(t.createdAt)} · Quelle{' '}
            {(
              {
                PORTAL: 'Kundenportal',
                MANUAL: 'manuell',
                FORM: 'Formular',
                EMAIL: 'E-Mail',
                MONITORING: 'Monitoring (Zabbix)',
                WHATSAPP: 'WhatsApp',
              } as Record<string, string>
            )[t.source] ?? t.source}
            {t.requesterName && ` · von ${t.requesterName}`}
          </p>
        </div>
        <div className="flex gap-2">
          <PrintButton documents={[{ key: 'ticket', label: 'Ticket' }]} target={{ ticketId: t.id }} />
          {canUpdate && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil /> Bearbeiten
            </Button>
          )}
          {can('tickets.delete') && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={async () => {
                if (
                  await confirm({
                    title: `Ticket ${t.number} löschen?`,
                    description: 'Verlauf, Kommentare und Anhänge werden unwiderruflich gelöscht.',
                    confirmLabel: 'Löschen',
                    destructive: true,
                  })
                )
                  remove.mutate();
              }}
            >
              <Trash2 /> Löschen
            </Button>
          )}
        </div>
      </div>

      {canUpdate && t.allowedStatusIds.some((sid) => sid !== t.status.id) && (
        <div className="-mt-3 mb-6 flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Status ändern:</span>
          {meta.statuses
            .filter((s) => s.id !== t.status.id && t.allowedStatusIds.includes(s.id))
            .map((s) => (
              <Button
                key={s.id}
                size="sm"
                variant={s.category === 'RESOLVED' || s.category === 'CLOSED' ? 'default' : 'outline'}
                loading={patch.isPending && patch.variables?.statusId === s.id}
                disabled={patch.isPending}
                onClick={() => patch.mutate({ statusId: s.id })}
              >
                → {s.name}
              </Button>
            ))}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] 3xl:grid-cols-[minmax(0,1fr)_24rem] 4xl:grid-cols-[minmax(0,1fr)_50rem]">
        <div className="flex min-w-0 flex-col gap-4">
          <Card>
            <CardContent className="whitespace-pre-wrap break-words pt-5 text-sm leading-relaxed">{t.description}</CardContent>
          </Card>

          {t.attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Anhänge</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="flex flex-col gap-1.5">
                  {t.attachments.map((a) => (
                    <li key={a.id} className="flex items-center gap-2 text-sm">
                      <a href={`/api/tickets/${t.id}/attachments/${a.id}`} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-2 hover:underline">
                        <Download className="size-4 shrink-0 text-muted-foreground" />
                        <span className="truncate">{a.file.originalName}</span>
                      </a>
                      <span className="text-xs text-muted-foreground">{formatBytes(a.file.size)}</span>
                      {a.visibility === 'INTERNAL' && <Badge variant="warning">intern</Badge>}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <section aria-label="Verlauf" className="flex flex-col gap-3">
            {timeline.map((item) =>
              item.kind === 'comment' ? (
                <div
                  key={item.comment.id}
                  className={cn(
                    'rounded-xl border p-4',
                    item.comment.visibility === 'INTERNAL' && 'border-amber-300/70 bg-amber-50/70 dark:border-amber-700/50 dark:bg-amber-950/20',
                    item.comment.isCustomer && 'border-primary/30 bg-primary/5',
                  )}
                >
                  <div className="mb-1.5 flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{item.comment.authorName ?? 'System'}</span>
                    {item.comment.isCustomer && <Badge>Kunde</Badge>}
                    {item.comment.visibility === 'INTERNAL' && (
                      <Badge variant="warning">
                        <Lock className="size-3" /> intern
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground" title={formatDateTime(item.comment.createdAt)}>
                      {formatRelative(item.comment.createdAt)}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm">{item.comment.body}</p>
                </div>
              ) : (
                <div key={item.event.id} className="flex items-start gap-2 px-1 text-xs text-muted-foreground">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-border" />
                  <span>
                    <span className="font-medium text-foreground/80">{item.event.actorName ?? 'System'}</span> {describeEvent(item.event.type, item.event.data, names)} ·{' '}
                    <span title={formatDateTime(item.event.createdAt)}>{formatRelative(item.event.createdAt)}</span>
                  </span>
                </div>
              ),
            )}
          </section>

          <Composer ticketId={t.id} canPublic={canUpdate} canInternal={can('tickets.comment_internal')} onDone={refresh} />
        </div>

        {/* Rechte Spalte: einklappbare Karten, auf großen Bildschirmen eigenständig scrollbar */}
        <aside className="lg:sticky lg:top-18 lg:max-h-[calc(100dvh-5.5rem)] lg:self-start lg:overflow-y-auto lg:pr-1">
          <CollapsibleCards scope="ticket" defaultOpen={['Kunde', 'Abrechnung']} className="flex flex-col gap-4 4xl:grid 4xl:grid-cols-2 4xl:content-start">
          <Card>
            <CardContent className="grid gap-3 pt-5">
              <Prop label="Status">
                <div>
                  <StatusBadge name={t.status.name} color={t.status.color} />
                </div>
              </Prop>
              <Prop label="Priorität">
                <Select value={t.priority} disabled={!canUpdate} onChange={(e) => patch.mutate({ priority: e.target.value as Priority })}>
                  <option value="LOW">Niedrig</option>
                  <option value="NORMAL">Normal</option>
                  <option value="HIGH">Hoch</option>
                  <option value="URGENT">Dringend</option>
                </Select>
              </Prop>
              <Prop label="Bearbeiter">
                {canAssign ? (
                  <Select value={t.assignee?.id ?? ''} onChange={(e) => patch.mutate({ assigneeId: e.target.value || null })}>
                    <option value="">– nicht zugewiesen –</option>
                    {meta.agents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span>{t.assignee?.name ?? '–'}</span>
                    {canUpdate && !t.assignee && (
                      <Button size="sm" variant="outline" onClick={() => patch.mutate({ assigneeId: me!.user.id })}>
                        Übernehmen
                      </Button>
                    )}
                  </div>
                )}
              </Prop>
              <Prop label="Team">
                <Select value={t.team?.id ?? ''} disabled={!canAssign} onChange={(e) => patch.mutate({ teamId: e.target.value || null })}>
                  <option value="">– kein Team –</option>
                  {meta.teams.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </Select>
              </Prop>
              <Prop label="Bereich">
                <Select value={t.area.id} disabled={!canUpdate} onChange={(e) => patch.mutate({ areaId: e.target.value })}>
                  {meta.areas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </Prop>
              <Prop label="Kategorie">
                <Select value={t.category?.id ?? ''} disabled={!canUpdate} onChange={(e) => patch.mutate({ categoryId: e.target.value || null })}>
                  <option value="">– keine –</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.parentId ? '↳ ' : ''}
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Prop>
              <Prop label="Fällig am">
                <Input
                  type="datetime-local"
                  disabled={!canUpdate}
                  defaultValue={toLocalInput(t.dueAt)}
                  key={`due-${t.dueAt}`}
                  onBlur={(e) => {
                    const v = e.target.value ? new Date(e.target.value).toISOString() : null;
                    if (v !== t.dueAt) patch.mutate({ dueAt: v });
                  }}
                />
              </Prop>
              <Prop label="Wiedervorlage">
                <Input
                  type="datetime-local"
                  disabled={!canUpdate}
                  defaultValue={toLocalInput(t.followUpAt)}
                  key={`fu-${t.followUpAt}`}
                  onBlur={(e) => {
                    const v = e.target.value ? new Date(e.target.value).toISOString() : null;
                    if (v !== t.followUpAt) patch.mutate({ followUpAt: v });
                  }}
                />
              </Prop>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Kunde</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              {billingEditable ? (
                <CustomerPicker value={t.customer} onChange={(c) => patch.mutate({ customerId: c?.id ?? null, contactId: null })} />
              ) : (
                <p className="text-sm">{t.customer?.name ?? '–'}</p>
              )}
              {t.customer && (
                <>
                  {customer && (
                    <Select value={t.contact?.id ?? ''} disabled={!billingEditable} onChange={(e) => patch.mutate({ contactId: e.target.value || null })} aria-label="Ansprechpartner">
                      <option value="">– kein Ansprechpartner –</option>
                      {customer.contacts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.firstName} {c.lastName}
                        </option>
                      ))}
                    </Select>
                  )}
                  {t.contact && (
                    <p className="text-xs text-muted-foreground">
                      {t.contact.email}
                      {t.contact.phone && ` · ${t.contact.phone}`}
                    </p>
                  )}
                  {can('customers.read') && (
                    <Link to={`/customers/${t.customer.id}`} className="text-sm text-primary hover:underline">
                      Kundenakte öffnen
                    </Link>
                  )}
                </>
              )}
              {!t.customer && t.requesterEmail && <p className="text-xs text-muted-foreground">Absender: {t.requesterEmail}</p>}
            </CardContent>
          </Card>

          <SlaCard t={t} />
          {t.source === 'MONITORING' && <MonitoringCard ticketId={t.id} />}
          <AppointmentsCard
            ticketId={t.id}
            defaults={{
              title: `${t.number}: ${t.subject}`.slice(0, 200),
              customerId: t.customer?.id,
              customerEmails: t.contact?.email ? [t.contact.email] : t.requesterEmail ? [t.requesterEmail] : [],
              technicianIds: t.assignee ? [t.assignee.id] : undefined,
            }}
          />
          <ChecklistsCard ticketId={t.id} />
          <WatchersCard ticketId={t.id} watchers={t.watchers} billingWatching={t.billingWatching} canUpdate={canUpdate} onChange={refresh} />
          {t.customer && <BillingCard ticketId={t.id} />}

          <TicketDevicesCard ticket={t} canUpdate={canUpdate} onChange={refresh} />

          {(t.repairs.length > 0 || can('repairs.intake')) && (
            <Card>
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-sm">Reparaturen</CardTitle>
                {can('repairs.intake') && t.customer && (
                  <Button asChild size="sm" variant="outline">
                    <Link
                      to={`/repairs/new?ticketId=${t.id}&customerId=${t.customer.id}&customerName=${encodeURIComponent(t.customer.name)}&fault=${encodeURIComponent(t.description.slice(0, 2000))}`}
                    >
                      Gerät annehmen
                    </Link>
                  </Button>
                )}
              </CardHeader>
              <CardContent className="grid gap-2">
                {t.repairs.map((r) => (
                  <Link key={r.id} to={`/repairs/${r.id}`} className="flex items-center justify-between gap-2 text-sm hover:underline">
                    <span className="font-mono">{r.number}</span>
                    <RepairStatusBadge status={r.status} />
                  </Link>
                ))}
                {t.repairs.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t.customer ? 'Noch keine Reparatur zu diesem Ticket.' : 'Für eine Reparatur bitte zuerst einen Kunden zuordnen.'}</p>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-1.5 text-sm">
                <Clock className="size-4" /> Zeiten
              </CardTitle>
              <span className="text-sm tabular-nums text-muted-foreground">{formatMinutes(totalMinutes)}</span>
            </CardHeader>
            <CardContent className="grid gap-2">
              {t.timeEntries.map((e) => (
                <div key={e.id} className="flex items-start justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <p>
                      {formatMinutes(e.minutes)} · <span className="text-muted-foreground">{e.user.name}</span>
                      {e.rateName && <span className="text-muted-foreground"> · {e.rateName}</span>}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {formatDate(e.workDate)}
                      {e.description && ` · ${e.description}`}
                    </p>
                  </div>
                  {billingEditable && (
                    <button className="text-muted-foreground hover:text-destructive" onClick={() => delTime.mutate(e.id)} aria-label="Zeitbuchung löschen">
                      <X className="size-4" />
                    </button>
                  )}
                </div>
              ))}
              {canUpdate && !billingEditable && <p className="text-xs text-muted-foreground">Zur Abrechnung freigegeben – Zeiten nur nach genehmigtem Änderungsantrag.</p>}
              {billingEditable && (
                <form
                  className="mt-1 grid grid-cols-[5rem_1fr_auto] gap-2 [&>select]:col-span-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (Number(minutes) > 0) addTime.mutate();
                  }}
                >
                  <Input type="number" min={1} placeholder="Min." value={minutes} onChange={(e) => setMinutes(e.target.value)} aria-label="Minuten" />
                  <Input placeholder="Tätigkeit" value={timeNote} onChange={(e) => setTimeNote(e.target.value)} aria-label="Tätigkeit" />
                  <RateSelect value={rateId} onChange={setRateId} className="h-9 text-sm" />
                  <Button type="submit" size="sm" variant="outline" className="h-9" disabled={!(Number(minutes) > 0)} loading={addTime.isPending}>
                    Buchen
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-1.5 text-sm">
                <Link2 className="size-4" /> Verknüpfte Tickets
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2">
              {t.links.map((l) => (
                <div key={l.ticket.id} className="flex items-center justify-between gap-2 text-sm">
                  <Link to={`/tickets/${l.ticket.id}`} className="min-w-0 truncate hover:underline">
                    <span className="font-mono text-xs text-muted-foreground">{l.ticket.number}</span> {l.ticket.subject}
                  </Link>
                  {canUpdate && (
                    <button className="text-muted-foreground hover:text-destructive" onClick={() => delLink.mutate(l.ticket.id)} aria-label="Verknüpfung entfernen">
                      <X className="size-4" />
                    </button>
                  )}
                </div>
              ))}
              {canUpdate && (
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (linkNumber.trim()) addLink.mutate();
                  }}
                >
                  <Input placeholder="Ticketnummer, z. B. T-000012" value={linkNumber} onChange={(e) => setLinkNumber(e.target.value)} aria-label="Ticketnummer" />
                  <Button type="submit" size="sm" variant="outline" className="h-9" disabled={!linkNumber.trim()} loading={addLink.isPending}>
                    Verknüpfen
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
          </CollapsibleCards>
        </aside>
      </div>
      <EditDialog key={editOpen ? 'open' : 'closed'} ticket={t} open={editOpen} onOpenChange={setEditOpen} onSave={(p) => patch.mutate(p)} />
    </>
  );
}
